import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Image,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MdFileDownload } from 'react-icons/md';
import {
  AppCard,
  AppSelect,
  AnimatedSearchInput,
  OutlineButton,
  SortableTh,
  StatusBadge,
  TablePagination,
  TableLoader,
} from '../../../../components/ui';
import { useAdminHeaderRefresh } from '../../../../contexts/AdminHeaderRefreshContext';
import { listUserEquipment, exportUserEquipmentExcel } from '../../../../repositories/userEquipment.repository';
import { lookupEquipmentTypes } from '../../../../repositories/lookup.repository';
import { usePagination } from '../../../../hooks/usePagination';
import { useTableSort } from '../../../../hooks/useTableSort';
import { toApiKyc } from '../../../../models/userEquipment.model';

const KYC_STATUSES = ['Verified', 'Pending', 'Rejected'];

const FLEET_SEARCH_HINTS = [
  'Search by registration no.…',
  'Search by equipment type…',
  'Search by manufacturer / model…',
  'Search by member name…',
  'Search by phone…',
  'Search by chassis / engine…',
  'Search for village…',
  'Search by KYC status…',
];

function kycBadge(status) {
  if (status === 'Verified') return { status: 'verified' };
  if (status === 'Rejected') return { status: 'error' };
  return { status: 'pending' };
}

function matchesFleetSearch(asset, q) {
  if (!q) return true;
  const hay = [
    asset.assetId,
    asset.equipmentType,
    asset.equipmentTypeCategory,
    asset.vehicleClass,
    asset.regNo,
    asset.manufacturer,
    asset.model,
    asset.variant,
    asset.fuel,
    asset.color,
    asset.chassisNumber,
    asset.engineNumber,
    asset.ownerName,
    asset.ownerPhone,
    asset.villageName,
    asset.status,
    asset.yom,
    asset.capacity,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

export default function EquipmentFleetPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [assets, setAssets] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [equipmentTypeFilter, setEquipmentTypeFilter] = useState('');
  const [kycFilter, setKycFilter] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [assetRes, typeRes] = await Promise.all([
        listUserEquipment({ pageSize: 200 }),
        lookupEquipmentTypes(),
      ]);
      setAssets(assetRes.items || []);
      setEquipmentTypes(typeRes || []);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load equipment',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  useAdminHeaderRefresh(load);

  const equipmentTypeOptions = useMemo(
    () =>
      equipmentTypes
        .filter((e) => e.active !== false)
        .map((e) => ({ value: e.name, label: e.name })),
    [equipmentTypes],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter((asset) => {
      const matchQ = matchesFleetSearch(asset, q);
      const matchType =
        !equipmentTypeFilter || asset.equipmentType === equipmentTypeFilter;
      const matchKyc = !kycFilter || (asset.status || 'Pending') === kycFilter;
      return matchQ && matchType && matchKyc;
    });
  }, [assets, search, equipmentTypeFilter, kycFilter]);

  const { sorted, sort, toggleSort } = useTableSort(rows, {
    assetId: (r) => r.assetId,
    type: (r) => r.equipmentType,
    makeModel: (r) => [r.manufacturer, r.model].filter(Boolean).join(' '),
    regNo: (r) => r.regNo,
    owner: (r) => r.ownerName,
    village: (r) => r.villageName,
    kyc: (r) => r.status || 'Pending',
  });

  const {
    pageItems,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
  } = usePagination(sorted);

  const handleExport = async () => {
    setExporting(true);
    try {
      const matchedType = equipmentTypes.find(
        (t) => t.name === equipmentTypeFilter,
      );
      await exportUserEquipmentExcel({
        search: search.trim() || undefined,
        equipmentTypeId: matchedType?.id || undefined,
        kycStatus: kycFilter ? toApiKyc(kycFilter) : undefined,
      });
      toast({
        title: 'Export started',
        description: 'All equipment matching current filters were included.',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Export failed',
        status: 'warning',
        position: 'top-right',
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <AppCard mb={4}>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3} alignItems="end">
          <AnimatedSearchInput
            hints={FLEET_SEARCH_HINTS}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <AppSelect
            label="Equipment type"
            labelFontSize="13px"
            h="40px"
            placeholder="All types"
            value={equipmentTypeFilter}
            onChange={(v) => setEquipmentTypeFilter(v || '')}
            options={[
              { value: '', label: 'All types' },
              ...equipmentTypeOptions,
            ]}
          />
          <AppSelect
            label="KYC"
            labelFontSize="13px"
            h="40px"
            placeholder="All"
            value={kycFilter}
            onChange={(v) => setKycFilter(v || '')}
            options={[
              { value: '', label: 'All' },
              ...KYC_STATUSES.map((s) => ({ value: s, label: s })),
            ]}
          />
          <Flex gap={2} align="end" w={{ base: '100%', md: 'auto' }} flexShrink={0}>
            <OutlineButton
              size="sm"
              h="40px"
              minH="40px"
              minW="132px"
              px={4}
              onClick={() => {
                setSearch('');
                setEquipmentTypeFilter('');
                setKycFilter('');
              }}
            >
              Clear filters
            </OutlineButton>
            <OutlineButton
              size="sm"
              h="40px"
              minH="40px"
              minW="132px"
              px={4}
              leftIcon={<MdFileDownload />}
              onClick={handleExport}
              isLoading={exporting}
              isDisabled={loading || exporting}
            >
              Export Excel
            </OutlineButton>
          </Flex>
        </SimpleGrid>
      </AppCard>

      <AppCard p={0} overflow="hidden">
        <Box px={6} pt={5} pb={3}>
          <Text fontSize="13px" color="text.secondary">
            {totalCount} equipment{totalCount === 1 ? '' : 's'}
          </Text>
        </Box>
        {loading ? (
          <TableLoader />
        ) : (
          <>
            <Box overflowX="auto">
              <Table variant="simple" size="md">
                <Thead bg="bg.input">
                  <Tr>
                    <SortableTh columnKey="type" sort={sort} onSort={toggleSort}>
                      Type
                    </SortableTh>
                    <SortableTh columnKey="makeModel" sort={sort} onSort={toggleSort}>
                      Make / model
                    </SortableTh>
                    <SortableTh columnKey="regNo" sort={sort} onSort={toggleSort}>
                      Reg. no.
                    </SortableTh>
                    <SortableTh columnKey="owner" sort={sort} onSort={toggleSort}>
                      Member
                    </SortableTh>
                    <SortableTh columnKey="village" sort={sort} onSort={toggleSort}>
                      Village
                    </SortableTh>
                    <SortableTh columnKey="kyc" sort={sort} onSort={toggleSort}>
                      KYC
                    </SortableTh>
                  </Tr>
                </Thead>
                <Tbody>
                  {pageItems.map((asset) => (
                    <Tr
                      key={asset.id}
                      cursor="pointer"
                      _hover={{ bg: 'bg.input' }}
                      onClick={() => navigate(`/admin/equipments/fleet/${asset.id}`)}
                    >
                      <Td>
                        <Flex align="center" gap={3}>
                          {asset.thumbnailUrl ? (
                            <Image
                              src={asset.thumbnailUrl}
                              alt=""
                              boxSize="40px"
                              borderRadius="sm"
                              objectFit="cover"
                              flexShrink={0}
                            />
                          ) : (
                            <Box
                              boxSize="40px"
                              borderRadius="sm"
                              bg="bg.input"
                              borderWidth="1px"
                              borderColor="border.subtle"
                              flexShrink={0}
                            />
                          )}
                          <Text fontWeight="600">{asset.equipmentType || '—'}</Text>
                        </Flex>
                      </Td>
                      <Td>
                        {[asset.manufacturer, asset.model].filter(Boolean).join(' · ') ||
                          '—'}
                      </Td>
                      <Td>{asset.regNo || '—'}</Td>
                      <Td>
                        <Text fontWeight="600">{asset.ownerName || '—'}</Text>
                        <Text fontSize="12px" color="text.secondary">
                          {asset.ownerPhone || ''}
                        </Text>
                      </Td>
                      <Td>{asset.villageName || '—'}</Td>
                      <Td>
                        <StatusBadge {...kycBadge(asset.status)}>
                          {asset.status || 'Pending'}
                        </StatusBadge>
                      </Td>
                    </Tr>
                  ))}
                  {pageItems.length === 0 ? (
                    <Tr>
                      <Td colSpan={6}>
                        <Text color="text.secondary" py={8} textAlign="center">
                          No equipment matches your filters
                        </Text>
                      </Td>
                    </Tr>
                  ) : null}
                </Tbody>
              </Table>
            </Box>
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </AppCard>
    </Box>
  );
}
